// Admin → Routing rules' release review: the groups (customers sharing a shown name) whose routing
// window changed, unasked, when routing rules were released — a code counting with the group's
// strictest instead of the window it had alone (`releaseReview`). Each group is decided once, with
// the rename dialog's three choices: keep the strictest (as now), set the group's own window, or keep
// their own (every code kept separate). The choice saves through the normal paths (a System log entry
// each, restorable), then a "review" entry marks the group reviewed (`reviewText`), and it leaves the list.

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  NO_RULE,
  ROUTING_RULES,
  checkRule,
  countText,
  hasRule,
  aloneText,
  inUseText,
  reviewText,
  ruleText,
  valuesOf,
} from "@app/shared";
import type { ReviewGroup, RoutingRule, RoutingRuleRow } from "@app/shared";
import { Button, Checkbox, Input, crudErrorText } from "@axny/platform-ui";
import { useLoggedWrites } from "./logged-write";
import { notify, useMessage } from "./message";
import { useRuleWrites } from "./routing-rule-writes";
import { SourceBadge } from "./source-filter";

/** The banner, and the list it opens. */
export function ReleaseReview({ groups, rows }: { groups: readonly ReviewGroup[]; rows: readonly RoutingRuleRow[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="release-review">
      <div className="flex flex-wrap items-center gap-2 text-sm text-amber-900">
        <span className="font-medium">
          {countText(groups.length, "group")} changed {groups.length === 1 ? "its" : "their"} routing window when
          routing rules were released
        </span>
        <span className="text-amber-800">
          — customers sharing a shown name now count with the strictest of their windows
        </span>
        <Button size="sm" variant="outline" className="ml-auto" onClick={() => setOpen((o) => !o)}>
          {open ? "Hide" : "Review"}
        </Button>
      </div>
      {open ? groups.map((g) => <ReviewCard key={g.shown} group={g} rows={rows} />) : null}
    </div>
  );
}

function ReviewCard({ group, rows }: { group: ReviewGroup; rows: readonly RoutingRuleRow[] }) {
  const message = useMessage();
  const queryClient = useQueryClient();
  const logged = useLoggedWrites();
  const writes = useRuleWrites();
  const [busy, setBusy] = useState(false);
  const [setting, setSetting] = useState(false);
  const [typed, setTyped] = useState({
    open: group.strictest.openBdays === null ? "" : String(group.strictest.openBdays),
    due: group.strictest.dueBdays === null ? "" : String(group.strictest.dueBdays),
    notes: "",
    none: false,
  });
  const merged = group.codes.filter((c) => !c.separate);
  const recordOf = (key: string) => rows.flatMap((r) => r.codes).find((c) => c.key === key)?.record ?? null;

  /** The choice's saves, one code after another, then the review's own entry. */
  const decide = async (what: string, save: ((key: string) => Promise<void>) | null) => {
    setBusy(true);
    const failed: string[] = [];
    try {
      if (save)
        for (const c of merged) {
          try {
            await save(c.key);
          } catch (e) {
            failed.push(`${c.source} ${c.code} · ${c.name} — ${crudErrorText(e)}`);
          }
        }
      if (failed.length)
        return void (await message.error(
          `${failed.length} of ${merged.length} not saved`,
          "The group stays in the review: try again.",
          failed,
        ));
      await logged.note(ROUTING_RULES, "release-review", {
        action: "review",
        lineText: reviewText(group.shown),
        changes: [],
        note: `Release review: ${what} (codes ${group.codes.map((c) => c.code).join(", ")})`,
      });
      notify(`${group.shown}: reviewed`, what);
    } finally {
      setBusy(false);
      await queryClient.invalidateQueries({ queryKey: ["routing"] });
    }
  };
  const target = (key: string) => {
    const c = group.codes.find((x) => x.key === key)!;
    return { source: c.source, code: c.code, name: c.name, record: recordOf(key) };
  };
  const strictest = () => decide(`kept the strictest, ${ruleText(group.strictest)}`, null);
  const ownWindows = () =>
    decide("kept their own routing windows (each code kept separate)", (key) =>
      writes.save(
        target(key),
        { ...valuesOf(recordOf(key)), separate: true },
        String(recordOf(key)?.notes ?? ""),
        "Release review: kept its own routing window",
      ),
    );
  const setWindow = async () => {
    let rule: RoutingRule = NO_RULE;
    if (!typed.none) {
      const check = checkRule(typed.open, typed.due);
      if (!check.ok) return void (await message.error("Check the routing window", check.message));
      if (!hasRule(check.rule))
        return void (await message.error("Type Window opens or Route by", "Or tick No routing rule."));
      if (
        check.warning &&
        !(await message.confirm(check.warning.title, check.warning.text, { confirmLabel: "Save it anyway" }))
      )
        return;
      rule = check.rule;
    }
    await decide(`set the routing window to ${typed.none ? "No routing rule" : ruleText(rule)}`, (key) =>
      writes.save(
        target(key),
        { ...rule, noRule: typed.none, separate: false },
        typed.notes.trim(),
        "Release review: the group's window",
      ),
    );
  };

  return (
    <div className="space-y-2 rounded-md border bg-white p-3" data-testid="release-review-group">
      <p className="text-sm">
        <span className="font-semibold">{group.shown}</span>
        <span className="text-muted-foreground">
          {" "}
          · {countText(group.codes.length, "code")} · {countText(group.openLines, "open line")} · all count with{" "}
          {ruleText(group.strictest)} now
        </span>
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="text-muted-foreground text-left text-xs">
            <tr className="[&>th]:py-1 [&>th]:pr-3 [&>th]:font-medium">
              <th>Code</th>
              <th>Name</th>
              <th className="text-right">Open lines</th>
              <th>Window alone (before the release)</th>
              <th>Group's window now</th>
            </tr>
          </thead>
          <tbody>
            {group.codes.map((c) => (
              <tr key={c.key} className="border-t [&>td]:py-1 [&>td]:pr-3">
                <td className="whitespace-nowrap">
                  <SourceBadge source={c.source} /> <span className="font-mono">{c.code}</span>
                </td>
                <td>{c.name}</td>
                <td className="text-right tabular-nums">{c.openLines.toLocaleString("en-US")}</td>
                <td>{aloneText(c.alone)}</td>
                <td className={c.separate ? "text-muted-foreground" : undefined}>{inUseText(c.now)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {setting ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span>Window opens</span>
          <Input
            className="h-7 w-16 text-right tabular-nums"
            aria-label={`Window opens of ${group.shown}`}
            inputMode="numeric"
            maxLength={3}
            disabled={typed.none}
            value={typed.open}
            onChange={(e) => setTyped((t) => ({ ...t, open: e.target.value }))}
          />
          <span>Route by</span>
          <Input
            className="h-7 w-16 text-right tabular-nums"
            aria-label={`Route by of ${group.shown}`}
            inputMode="numeric"
            maxLength={3}
            disabled={typed.none}
            value={typed.due}
            onChange={(e) => setTyped((t) => ({ ...t, due: e.target.value }))}
          />
          <label className="flex items-center gap-1.5">
            <Checkbox checked={typed.none} onCheckedChange={(v) => setTyped((t) => ({ ...t, none: !!v }))} />
            No routing rule
          </label>
          <Input
            className="h-7 w-56"
            placeholder="Notes (why, who asked)"
            maxLength={500}
            value={typed.notes}
            onChange={(e) => setTyped((t) => ({ ...t, notes: e.target.value }))}
          />
          <Button size="sm" disabled={busy} onClick={() => void setWindow()}>
            Save the window
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setSetting(false)}>
            Back
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" disabled={busy} onClick={() => void strictest()}>
            Apply the strictest ({ruleText(group.strictest)})
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => setSetting(true)}>
            Set the routing window…
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void ownWindows()}>
            Keep their own routing windows
          </Button>
        </div>
      )}
    </div>
  );
}
